/**
 * Rollback configuration shared across pre-plan surfaces (dashboard row, attention modal, initiation
 * page). The authoritative window comes from the backend on the rollback plan
 * (`RollbackPlanResponse.rollbackWindowDays`); use that where the plan is available. This constant is
 * the fallback for contexts that run before the plan is fetched. Overridable per environment via
 * `VITE_ROLLBACK_WINDOW_DAYS`; defaults to the backend's 5-day window.
 */
export const ROLLBACK_WINDOW_DAYS: number = (() => {
    const raw = Number(import.meta.env['VITE_ROLLBACK_WINDOW_DAYS']);
    return Number.isFinite(raw) && raw > 0 ? raw : 5;
})();

export type RollbackBannerVariant = 'green' | 'amber' | 'red';

export interface RollbackBannerConfig {
    variant: RollbackBannerVariant;
    message: string;
}

/**
 * Builds the "time since migration" banner for a given number of days elapsed and the rollback
 * window. Thresholds scale with the window so the messaging is correct for any window length (not
 * just the legacy hardcoded 5-day one): green in the first ~40%, amber up to ~80%, red near/after
 * expiry.
 */
export function buildRollbackBannerConfig(days: number, windowDays: number): RollbackBannerConfig {
    const daysRemaining = Math.max(0, windowDays - days);
    const dayStr = (n: number) => `${n} day${n !== 1 ? 's' : ''}`;
    const fraction = windowDays > 0 ? days / windowDays : 1;

    if (days >= windowDays) {
        return {
            variant: 'red',
            message: `${dayStr(days)} since migration completion — automated rollback is no longer available.`,
        };
    }
    if (fraction <= 0.4) {
        return {
            variant: 'green',
            message: `${dayStr(days)} since migration completion — environment changes are unlikely. Automated rollback available for ${dayStr(daysRemaining)} more.`,
        };
    }
    if (fraction <= 0.8) {
        return {
            variant: 'amber',
            message: `${dayStr(days)} since migration completion — your environment may have changed since this migration ran. Automated rollback available for ${dayStr(daysRemaining)} more.`,
        };
    }
    return {
        variant: 'red',
        message: `${dayStr(days)} since migration completion — significant time has passed. Manual review is strongly recommended. Automated rollback available for ${dayStr(daysRemaining)} more.`,
    };
}
